import React, { Suspense, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  ToggleButton,
  Divider,
  Box,
  Typography,
  ListItemText,
  Slider,
} from '@mui/material';
import DawaAutocomplete from '../../../components/dawa/DawaAutocomplete';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { darken } from '@mui/material/styles';
import styled from '@emotion/styled';

function parseDaysOfWeek(dayString) {
  const allDays = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];
  const selected = (dayString || '').split(',').map((d) => d.trim());
  return Object.fromEntries(
    allDays.map((day) => [day, selected.includes(day)]),
  );
}

function serializeDaysOfWeek(daysObj) {
  return (
    Object.entries(daysObj)
      .filter(([_, selected]) => selected)
      .map(([day]) => day)
      .join(',') || null
  );
}

const InvertedSlider = styled(Slider, {
  shouldForwardProp: (prop) => prop !== 'isInverted',
})(({ theme, isInverted }) => ({
  color: '#4CAF50',
  height: 4,
  padding: '13px 0',
  '& .MuiSlider-thumb': {
    height: 28,
    width: 28,
    backgroundColor: '#fff',
    border: '1px solid #4CAF50',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 500,
    color: '#333',
    '&:hover': {
      boxShadow: '0 0 0 8px rgba(58, 133, 137, 0.16)',
    },
  },
  '& .MuiSlider-track': {
    height: 3,
    backgroundColor: isInverted ? '#d8d8d8' : '#4CAF50',
    borderRadius: 2,
    border: 'none',
  },
  '& .MuiSlider-rail': {
    height: 4,
    backgroundColor: isInverted ? '#4CAF50' : '#d8d8d8',
    opacity: 1,
    borderRadius: 2,
  },
  '& .MuiSlider-mark': {
    backgroundColor: '#888',
    height: 8,
    width: 2,
  },
  '& .MuiSlider-markActive': {
    backgroundColor: (theme) =>
      theme.palette.mode === 'light' ? 'gray' : 'lightgray',
  },
  '& .MuiSlider-valueLabel': {
    fontSize: 12,
    fontWeight: 'normal',
    top: -6,
    backgroundColor: 'unset',
    color: theme.palette.text.primary,
    '&::before': {
      display: 'none',
    },
    '& *': {
      background: 'transparent',
      color: '#000',
      ...theme.applyStyles('dark', {
        color: '#fff',
      }),
    },
  },
}));

const CreateEnergyGroupDialog = ({
  open,
  onClose,
  onCreate,
  triggerGetLocation,
  selectedAccount,
}) => {
  const [range, setRange] = useState([6, 18]); // Normal default
  const [isInverted, setIsInverted] = useState(false);

  const defaultDaysOfWeek = {
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  };

  const [newEnergyGroup, setNewGroup] = useState({
    name: '',
    accountId: selectedAccount?.AccountId,
    location: null,
    energyInitiatives: [],
    type: 'power',
    options: {
      workingDayStart: 6,
      workingDayEnd: 18,
      daysOfWeek: { ...defaultDaysOfWeek }, // <- use object, not null
      isDraftMode: false,
      goShares: [],
    },
  });

  useEffect(() => {
    if (selectedAccount) {
      setNewGroup((prev) => ({
        ...prev,
        accountId: selectedAccount.AccountId,
      }));
    }
  }, [selectedAccount]);

  const handleClose = () => {
    setRange([6, 18]); // Reset to default
    setNewGroup({
      name: '',
      accountId: selectedAccount?.AccountId,
      locationId: null,
      energyInitiatives: [],
      type: 'power',
      options: {
        workingDayStart: 6,
        workingDayEnd: 18,
        daysOfWeek: { ...defaultDaysOfWeek }, // reset to default object
        isDraftMode: false,
        goShares: [],
      },
    });
    setIsInverted(false);
    onClose();
  };

  const handleCreate = (group) => async () => {
    onCreate(group);
    handleClose();
  };

  const handleChange = (event, newValue) => {
    setRange(newValue);
    setNewGroup((prev) => ({
      ...prev,
      options: {
        ...prev.options,
        workingDayStart: newValue[0],
        workingDayEnd: newValue[1],
      },
    }));
  };

  const formatHour = (value) => {
    const hour = Math.floor(value);
    const remainder = (value % 1).toFixed(2);

    const minuteMap = {
      '0.00': ':00',
      0.25: ':15',
      '0.50': ':30',
      0.75: ':45',
    };

    const minutes = minuteMap[remainder] || ':00';
    return `${hour}${minutes}`;
  };

  const handleDayToggle = (dayKey) => {
    const current = newEnergyGroup.options.daysOfWeek || {};
    const updated = {
      ...current,
      [dayKey]: !current[dayKey],
    };

    setNewGroup((prev) => ({
      ...prev,
      options: {
        ...prev.options,
        daysOfWeek: updated,
      },
    }));
  };

  const isAnyDaySelected = Object.values(
    newEnergyGroup.options?.daysOfWeek || {},
  ).some(Boolean);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Ny energigruppe</DialogTitle>
      <DialogContent dividers>
        <TextField
          label="Navn"
          value={newEnergyGroup.name}
          required
          error={!newEnergyGroup.name}
          onChange={(e) =>
            setNewGroup({
              ...newEnergyGroup,
              name: e.target.value,
            })
          }
          fullWidth
          variant="filled"
          helperText="Giv gruppen et sigende navn"
        />
        <Suspense fallback={<div>Loading LocationSearchInput...</div>}>
          <DawaAutocomplete
            onLocationSelect={async (location) => {
              if (location && location.data) {
                try {
                  const result = await triggerGetLocation(
                    location.data.id,
                  ).unwrap();

                  if (result) {
                    const locationData = {
                      displayName:
                        result.adgangsadresse.adressebetegnelse || '',
                      street: result.adgangsadresse.vejstykke.navn,
                      houseNumber: result.adgangsadresse.husnr,
                      floor: result.etage || '', //should be optional
                      door: result.dør || '', //should be optional
                      zipCode: result.adgangsadresse.postnummer.nr,
                      city: result.adgangsadresse.postnummer.navn,
                      country: 'DK',
                      latitude:
                        result.adgangsadresse.adgangspunkt.koordinater[1],
                      longitude:
                        result.adgangsadresse.adgangspunkt.koordinater[0],
                      coordinateAccuracyMeters:
                        result.adgangsadresse.adgangspunkt.nøjagtighed,
                      coordinateSource:
                        result.adgangsadresse.adgangspunkt.kilde,
                      timeZone: 'Europe/Copenhagen',
                      energyPriceZone:
                        parseInt(result.adgangsadresse.postnummer.nr) < 5000
                          ? 'DK2'
                          : 'DK1', //gets parsed to either 1 or 2 in the database
                      municipalityCode: parseInt(
                        result.adgangsadresse.kommune.kode,
                      ),
                    };

                    setNewGroup((prev) => ({
                      ...prev,
                      location: locationData,
                    }));
                  }
                } catch (error) {
                  console.error('Error fetching location data:', error);
                }
              }
            }}
            value={newEnergyGroup.location?.displayName || ''}
            placeholder="Vælg adresse"
            disableField={false}
          />
        </Suspense>

        <Divider sx={{ my: 2 }} />
        <ListItemText
          primary="Ugedage"
          secondary="Vælg hvilke dage i ugen, hvor energi forbrug forventes."
        />

        {newEnergyGroup.options?.daysOfWeek && (
          <>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                justifyContent: 'center',
                flexWrap: 'wrap',
                mb: 2,
              }}
            >
              {[
                { key: 'monday', label: 'M' },
                { key: 'tuesday', label: 'T' },
                { key: 'wednesday', label: 'O' },
                { key: 'thursday', label: 'T' },
                { key: 'friday', label: 'F' },
                { key: 'saturday', label: 'L' },
                { key: 'sunday', label: 'S' },
              ].map(({ key, label }) => (
                <ToggleButton
                  key={key}
                  value={key}
                  selected={newEnergyGroup.options.daysOfWeek[key]}
                  error={!newEnergyGroup.options.daysOfWeek[key]}
                  onChange={() => handleDayToggle(key)}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1,
                    fontWeight: 'bold',
                    p: 0,
                    '&.Mui-selected': {
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: darken('#4CAF50', 0.1),
                      },
                    },
                  }}
                >
                  {label}
                </ToggleButton>
              ))}
            </Box>
            {!isAnyDaySelected && (
              <Typography variant="caption" color="error">
                Du skal vælge mindst én ugedag.
              </Typography>
            )}
          </>
        )}
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ListItemText
              sx={{ mb: 2 }}
              primary={'Vælg tidsrum'}
              secondary="Vælg et tidsrum, hvor energi forbrug forventes."
            />
          </Box>

          <Box>
            <InvertedSlider
              isInverted={isInverted}
              min={0}
              max={23}
              step={1}
              value={range}
              onChange={handleChange}
              valueLabelDisplay="on"
              valueLabelFormat={formatHour}
              marks={[
                { value: 0, label: '0' },
                { value: 4, label: '4' },
                { value: 6, label: '6' },
                { value: 8, label: '8' },
                { value: 10, label: '10' },
                { value: 12, label: '12' },
                { value: 14, label: '14' },
                { value: 16, label: '16' },
                { value: 18, label: '18' },
                { value: 20, label: '20' },
                { value: 23, label: '23' },
              ]}
              disableSwap
              sx={{
                mt: 2,
                mb: 8,
                '& .MuiSlider-thumb': {
                  '&::after': {
                    content: '""',
                    display: 'none',
                  },
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Afbryd
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate(newEnergyGroup)}
          disabled={
            !newEnergyGroup.name ||
            !newEnergyGroup.location ||
            !isAnyDaySelected
          }
        >
          Opret
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEnergyGroupDialog;
